import Grid from '@mui/material/Grid';
import { BaseSyntheticEvent, ReactElement, useState } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { useStoreActions, useStoreState } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error/error';
import { IShelterRequest } from '~/services/api/shelters/types';
import Button from '~/ui/components/common/Button';
import Input from '~/ui/components/inputs/Input';
import Select from '~/ui/components/inputs/SelectWithoutAnimation';
import InputMask from '~/ui/components/inputs/InputMask';

import { shelterAddressValidationSchema } from '~/ui/pages/Clients/reusable/HealthDetails/Forms/validates';

import { IOption } from '~/types';

import variables from '~/ui/assets/styles/colors.module.scss';
import styles from './ShelterModal.module.scss';

interface IProps {
  defaultValues?: IShelterRequest;
  states: IOption[];
  id?: string;
  onClose: () => void;
}

type IParams = {
  id?: string;
};

const Form = ({ defaultValues, states, id, onClose }: IProps): ReactElement => {
  const [processing, setProcessing] = useState(false);

  const { id: currentClinicId } = useParams<IParams>();

  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
    register,
  } = useForm({
    defaultValues,
    resolver: shelterAddressValidationSchema,
  });

  const user = useStoreState(state => state.user.current);

  const { onCreateShelter, onUpdateShelter } = useStoreActions(actions => actions.shelters);
  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);

  const clinicId = currentClinicId || String(user?.clinic?.id);

  const onSubmit = async (
    data: IShelterRequest,
    evt?: BaseSyntheticEvent<{ submitter: Pick<HTMLButtonElement, 'name'> }>,
  ) => {
    const { name } = evt?.nativeEvent?.submitter || {};
    const payload = {
      clinicId,
      data,
    };

    try {
      setProcessing(true);

      if (id) {
        await onUpdateShelter({ ...payload, id });
      } else {
        await onCreateShelter(payload);
      }

      const type = defaultValues ? 'updated' : 'added';

      reset({ name: null, phone: '', address: { zipCode: '', line1: '', line2: '', city: '' } });

      if (!name) {
        onClose();
      }

      showNotify({ message: `Shelter successfully ${type}` });
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className={styles.modalWrapper}>
      <h3 className={styles.modalTitle}>Add Shelter</h3>

      <form onSubmit={handleSubmit(onSubmit as SubmitHandler<FieldValues>)}>
        <Grid container spacing={2}>
          <Grid
            size={{
              sm: 12,
            }}
          >
            <Input register={register} name="name" errors={errors} label="Name" />
          </Grid>
          <Grid
            size={{
              sm: 12,
            }}
          >
            <InputMask control={control} name="phone" errors={errors} label="Phone" />
          </Grid>
          <Grid
            size={{
              sm: 12,
            }}
          >
            <Input register={register} name="address.city" errors={errors} label="City" />
          </Grid>
          <Grid
            size={{
              sm: 12,
            }}
          >
            <Input register={register} name="address.line1" errors={errors} label="Address 1" />
          </Grid>
          <Grid
            size={{
              sm: 12,
            }}
          >
            <Input register={register} name="address.line2" errors={errors} label="Address 2" />
          </Grid>
          <Grid
            size={{
              sm: 6,
            }}
          >
            <Select
              name="address.stateId"
              hideSelectedOptions={false}
              options={states}
              control={control}
              errors={errors}
              maxMenuHeight={150}
              label="Select State"
            />
          </Grid>
          <Grid
            size={{
              sm: 6,
            }}
          >
            <InputMask
              alwaysShowMask={false}
              mask="99999"
              name="address.zipCode"
              control={control}
              errors={errors}
              label="Zip Code"
            />
          </Grid>
        </Grid>
        <div className={styles.buttonsWrapper}>
          <Button
            color="primary"
            variant="outlined"
            className={styles.margin}
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </Button>
          {!defaultValues && (
            <Button
              color="primary"
              variant="outlined"
              type="submit"
              className={styles.margin}
              disabled={processing}
              name="Add Shelter"
            >
              Save And Add Another One
            </Button>
          )}
          <Button
            color="primary"
            variant="contained"
            type="submit"
            name={null}
            isLoading={processing}
            loaderColor={variables.colorGrey}
            disabled={processing}
          >
            {!defaultValues ? 'Add Shelter' : 'Update'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Form;
